import React, { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { useParams } from 'react-router-dom';
import NavbarAdmin from '../components/navbar/NavbarAdmin';
import Sidebar from '../components/sidebar/Sidebar';
import { getAdminProfile } from '../../api/admin';
import {
  sendOtpCode,
  verifyOtpCode,
  resetPassword,
} from '../../api/auth';

const SettingsScreen = () => {
  const { slug } = useParams();
  const [admin, setAdmin] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState('');

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const response = await getAdminProfile();
        setAdmin(response);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAdmin();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getPasswordStrength = (password) => {
    if (password.length < 8) return 'Faible';
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    if (hasLower && hasNumber && !hasUpper && !hasSpecial) return 'Faible';
    if (hasLower && hasUpper && hasNumber && !hasSpecial) return 'Moyen';
    if (hasLower && hasUpper && hasNumber && hasSpecial) return 'Fort';
    return 'Faible';
  };

  const getStrengthPercent = (strength) => {
    switch (strength) {
      case 'Faible': return 30;
      case 'Moyen': return 60;
      case 'Fort': return 100;
      default: return 0;
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'Faible': return 'bg-red-500';
      case 'Moyen': return 'bg-yellow-500';
      case 'Fort': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const openResetModal = async () => {
    setModalOpen(true);
    setStep(1);
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setPasswordStrength('');
    setConfirmError('');
    try {
      await sendOtpCode(admin.email);
      setResendCooldown(30);
    } catch {
      swal('Erreur', "Échec de l'envoi du code OTP.", 'error');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await sendOtpCode(admin.email);
      setResendCooldown(30);
      swal('Succès', 'Nouveau code OTP envoyé.', 'success');
    } catch {
      swal('Erreur', "Échec du renvoi du code OTP.", 'error');
    }
  };

  const handleVerifyOtp = async () => {
    try {
      await verifyOtpCode(admin.email, otp);
      setStep(2);
      setError('');
      swal('Succès', 'Code OTP vérifié avec succès.', 'success');
    } catch {
      swal('Erreur', 'Code OTP invalide.', 'error');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setConfirmError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (passwordStrength === 'Faible') {
      setError('Le mot de passe est trop faible.');
      return;
    }

    try {
      await resetPassword(admin.email, newPassword);
      swal('Succès', 'Mot de passe réinitialisé avec succès.', 'success');
      setModalOpen(false);
    } catch {
      swal('Erreur', 'Erreur lors de la réinitialisation du mot de passe.', 'error');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="fixed top-0 w-full md:hidden z-10">
        <NavbarAdmin slug={slug} />
      </div>
      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>
      <main className="flex-1 md:ml-64 p-6 pt-20 md:pt-6">
        <h1 className="text-2xl font-bold mb-4 text-paragaia">Paramètres du compte</h1>
        <p className="text-gray-700 mb-6">Informations personnelles de l’administrateur.</p>

        {admin && (
          <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Nom</label>
              <input
                type="text"
                value={admin.name}
                disabled
                className="w-full bg-gray-100 border border-gray-300 rounded px-4 py-2"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Email</label>
              <input
                type="email"
                value={admin.email}
                disabled
                className="w-full bg-gray-100 border border-gray-300 rounded px-4 py-2"
              />
            </div>
            <button
              onClick={openResetModal}
              className="bg-paragaia text-white font-semibold px-6 py-2 rounded hover:bg-paragaiaDark transition"
            >
              Réinitialiser le mot de passe
            </button>
          </div>
        )}

        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md space-y-6">
              <h2 className="text-2xl font-bold text-center text-paragaia">🔐 Réinitialisation du mot de passe</h2>

              {step === 1 && (
                <>
                  <p className="text-gray-600 text-center">Un code OTP a été envoyé à votre adresse email.</p>
                  
                  <input
                    type="text"
                    placeholder="Entrez le code OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full mt-4 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-paragaia"
                  />
                  <button
                    onClick={handleVerifyOtp}
                    className="w-full bg-paragaia text-white py-2 mt-4 rounded-lg hover:bg-paragaiaDark transition"
                  >
                    Vérifier le code
                  </button>
                  <button
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0}
                    className={`w-full mt-4 py-2 rounded-lg font-semibold text-gray-500 transition ${
                      resendCooldown > 0
                        ? 'bg-gray-200 cursor-not-allowed'
                        : 'bg-gray-300 hover:bg-gray-300'
                    }`}

                  >
                    {resendCooldown > 0
                      ? `Renvoyer le code OTP (${resendCooldown}s)`
                      : 'Renvoyer le code OTP'}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordStrength(getPasswordStrength(e.target.value));
                      setConfirmError('');
                    }}
                    className="w-full mt-2 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-paragaia"
                    placeholder="Nouveau mot de passe"
                  />
                  <div className="w-full bg-gray-200 h-2 rounded-full mt-2">
                    <div
                      className={`h-2 rounded-full ${getStrengthColor(passwordStrength)}`}
                      style={{ width: `${getStrengthPercent(passwordStrength)}%` }}
                    />
                  </div>
                  {passwordStrength && (
                    <p className={`text-sm mt-1 ${getStrengthColor(passwordStrength).replace('bg', 'text')}`}>
                      Force du mot de passe : {passwordStrength}
                    </p>
                  )}
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (newPassword !== e.target.value) {
                        setConfirmError('Les mots de passe ne correspondent pas.');
                      } else {
                        setConfirmError('');
                      }
                    }}
                    className="w-full mt-4 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-paragaia"
                    placeholder="Confirmer le mot de passe"
                  />
                  {confirmError && <p className="text-red-500 text-sm mt-1">{confirmError}</p>}
                  <button
                    onClick={handleResetPassword}
                    disabled={
                      passwordStrength === 'Faible' || confirmPassword !== newPassword
                    }
                    className={`w-full mt-4 py-2 rounded-lg font-semibold text-white transition ${
                      passwordStrength === 'Faible' || confirmPassword !== newPassword
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-paragaia hover:bg-paragaiaDark'
                    }`}
                  >
                    Réinitialiser le mot de passe
                  </button>
                </>
              )}

              <button
                onClick={() => setModalOpen(false)}
                className="block mx-auto text-sm text-gray-500 hover:underline mt-2"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SettingsScreen;
